import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Body1, Body2, Select, SelectTrigger } from "@activecollab/components";
import PropTypes from "prop-types";
import BalanceTooltipContent from "~/components/Subscription/SubscriptionPlanDetails/BalanceTooltipContent";
import SubscriptionInfoIconWithTooltip from "~/components/Subscription/SubscriptionPlanDetails/SubscriptionInfoIconWithTooltip";
import { editBillingOrder } from "~/store/actions/actions.billingOrder";
import { fetchSubscriptionBalance } from "~/store/actions/actions.subscriptionBalance";
import { getAmountAvailable, getCombinedRecords } from "~/store/selectors/selector.accountBalance";
import {
  canSwitchBillingPeriod,
  getBillingOrderPeriod,
  getIsAccountBalanceBillingOrder,
  getIsAddOnBillingOrder,
  makeBillingOrderForOrderSummarySelector,
  makeBillingOrderTaxForOrderSummarySelector,
} from "~/store/selectors/selector.billingOrder";
import { roundAmount } from "~/utils/utils";

const OrderSummary = ({ className, canUserChangeBillingPeriod }) => {
  const dispatch = useDispatch();
  const billingOrderSelector = makeBillingOrderForOrderSummarySelector();
  const billingOrderSummary = useSelector((state) => billingOrderSelector(state));
  const currentBalance = useSelector((state) => getAmountAvailable(state));
  const makeBillingOrderTax = useMemo(() => {
    return makeBillingOrderTaxForOrderSummarySelector();
  }, []);
  const billingOrderTaxSummary = useSelector(makeBillingOrderTax);
  const records = useSelector((state) => getCombinedRecords(state));
  const billingPeriod = useSelector(getBillingOrderPeriod);
  const canOrderChangeBillingPeriod = useSelector((state) => canSwitchBillingPeriod(state));
  const isAccountBalanceBillingOrder = useSelector((state) => getIsAccountBalanceBillingOrder(state));
  const isAddOnBillingOrder = useSelector(getIsAddOnBillingOrder);
  useEffect(() => {
    dispatch(fetchSubscriptionBalance());
  }, [dispatch]);

  const onBillingPeriodChange = useCallback(
    (billingPeriod) => {
      dispatch(editBillingOrder({ billing_period: billingPeriod }));
    },
    [dispatch]
  );

  const newBalance = useMemo(() => {
    return Math.max(0, currentBalance - billingOrderSummary?.subtotal + billingOrderSummary?.discount);
  }, [billingOrderSummary?.discount, billingOrderSummary?.subtotal, currentBalance]);

  return !billingOrderSummary?.id ? null : (
    <div className={className}>
      <div
        className="tw-flex tw-justify-between"
        data-testid="order-summary-plan"
      >
        <Body2 color="primary">
          {billingOrderSummary.orderSummary.split(",").map((summaryItem, index) => {
            return (
              <div key={index}>
                <div>{summaryItem}</div>
              </div>
            );
          })}
        </Body2>
        <Body2>${billingOrderSummary.subtotal}</Body2>
      </div>

      {isAddOnBillingOrder || !canOrderChangeBillingPeriod ? null : (
        <div
          className="tw-flex tw-justify-between tw-mt-4"
          data-testid="order-summary-billing-period"
        >
          <Body2
            color="primary"
            className="tw-flex tw-justify-center tw-justify-items-center tw-flex-col"
          >
            Billing Period
          </Body2>
          {canUserChangeBillingPeriod ? (
            <Select
              options={[
                { id: "monthly", name: "Monthly" },
                { id: "annually", name: "Annual" },
              ]}
              selected={billingPeriod}
              target={
                <SelectTrigger
                  className="tw-w-auto"
                  size="small"
                >
                  {billingPeriod === "monthly" ? "Monthly" : "Annual"}
                </SelectTrigger>
              }
              disabledInternalSort
              disableSearch
              onChange={onBillingPeriodChange}
              forceCloseMenu
              mode="tiny"
            />
          ) : (
            <Body2 color="primary">{billingPeriod === "monthly" ? "Monthly" : "Annual"}</Body2>
          )}
        </div>
      )}

      {billingOrderSummary?.discount ? (
        <div
          className="tw-flex tw-justify-between tw-mt-4"
          data-testid="order-summary-discount"
        >
          <Body2 color="primary">Discount</Body2>
          <Body2 color="primary">${roundAmount(billingOrderSummary.discount)}</Body2>
        </div>
      ) : null}

      {records.length > 0 ? (
        <div
          className="tw-flex tw-justify-between tw-mt-4"
          data-testid="order-summary-balance"
        >
          <div className="tw-flex tw-items-center">
            <Body2
              color="primary"
              className="tw-mr-2"
            >
              Current Balance
            </Body2>
            <SubscriptionInfoIconWithTooltip
              title={<BalanceTooltipContent />}
              wide
            />
          </div>
          <Body2>${currentBalance}</Body2>
        </div>
      ) : null}

      <div
        className="tw-flex tw-justify-between tw-mt-4"
        data-testid="order-summary-subtotal"
      >
        <Body2 color="primary">Subtotal</Body2>
        <Body2>
          {isAccountBalanceBillingOrder ? (
            <>${roundAmount(Math.max(0, billingOrderSummary.subtotal - billingOrderSummary.discount))}</>
          ) : (
            <>
              $
              {roundAmount(
                Math.max(
                  0,
                  billingOrderSummary.subtotal -
                    billingOrderSummary.discount -
                    (currentBalance > 0 ? currentBalance : 0)
                )
              )}
            </>
          )}
        </Body2>
      </div>

      {Number(billingOrderTaxSummary?.tax) ? (
        <div
          className="tw-flex tw-justify-between tw-mt-4"
          data-testid="order-summary-tax"
        >
          <Body2 color="primary">Tax - {billingOrderTaxSummary.taxRate}%</Body2>
          <Body2 align="right">${roundAmount(billingOrderTaxSummary.tax)}</Body2>
        </div>
      ) : null}

      <div
        className="tw-flex tw-justify-between tw-mt-4"
        data-testid="order-summary-total"
      >
        <Body1
          color="primary"
          weight="bold"
        >
          Total
        </Body1>
        <Body1
          weight="bold"
          color="primary"
        >
          ${roundAmount(billingOrderSummary.total)}
        </Body1>
      </div>

      {newBalance > 0 ? (
        <div
          className="tw-flex tw-justify-between tw-mt-4"
          data-testid="order-summary-new-balance"
        >
          <Body2 color="primary">New Balance</Body2>
          <Body2>${roundAmount(newBalance)}</Body2>
        </div>
      ) : null}
    </div>
  );
};

OrderSummary.propTypes = {
  className: PropTypes.string,
  canUserChangeBillingPeriod: PropTypes.bool,
};

OrderSummary.defaultProps = {
  canUserChangeBillingPeriod: true,
};

OrderSummary.displayName = `OrderSummary`;

export default OrderSummary;
